import React, { useState } from 'react';
import LanguageSwitcher from './ui/LanguageSwitcher';
import { t } from '../utils/i18n';
import { getLocalizedPath } from '../utils/navigation';
import { 
  Home, 
  Briefcase, 
  Building, 
  RefreshCw, 
  Tractor, 
  Wheat, 
  Car, 
  Facebook, 
  Linkedin, 
  MapPin, 
  Clock, 
  Phone, 
  Menu, 
  X, 
  ChevronDown, 
  ChevronUp 
} from 'lucide-react';

// Icon components using Lucide React
const Icon = ({ name, className = "w-5 h-5" }: { name: string; className?: string }) => {
  const icons: { [key: string]: JSX.Element } = {
    home: <Home className={className} />,
    briefcase: <Briefcase className={className} />,
    suitcase: <Briefcase className={className} />,
    building: <Building className={className} />,
    refresh: <RefreshCw className={className} />,
    tractor: <Tractor className={className} />,
    crop: <Wheat className={className} />,
    car: <Car className={className} />,
    facebook: <Facebook className={className} />,
    linkedin: <Linkedin className={className} />,
    map: <MapPin className={className} />,
    clock: <Clock className={className} />,
    phone: <Phone className={className} />,
    menu: <Menu className={className} />,
    close: <X className={className} />,
    chevronDown: <ChevronDown className={className} />,
    chevronUp: <ChevronUp className={className} />,
  };

  return icons[name] || null;
};

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleNavClick = (href: string, e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    // Check if we need to redirect to a different page
    const currentPath = window.location.pathname;
    const isHomePage = currentPath === '/' || currentPath.match(/^\/[a-z]{2}$/); // root or locale root
    
    console.log('handleNavClick:', { href, currentPath, isHomePage });
    
    if (href === '#home') {
      if (isHomePage) {
        console.log('Scrolling to top');
        scrollToTop();
      } else {
        // Redirect to home page
        const homeUrl = getLocalizedPath('/');
        console.log('Redirecting to home:', homeUrl);
        console.log('Current locale from getCurrentLanguage():', getCurrentLanguage());
        window.location.assign(homeUrl);
      }
    } else if (href.startsWith('#')) {
      // Handle anchor links
      if (isHomePage) {
        // We're on the home page, scroll to element
        console.log('Looking for element:', href);
        const element = document.querySelector(href);
        if (element) {
          console.log('Element found, scrolling to it');
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        } else {
          console.log('Element not found:', href, 'Available elements:', document.querySelectorAll('[id]'));
          // If element not found, try to scroll to top as fallback
          scrollToTop();
        }
      } else {
        // We're not on the home page, redirect to home page with anchor
        const redirectUrl = getLocalizedPath('/') + href;
        console.log('Redirecting to home with anchor:', redirectUrl);
        console.log('Current locale from getCurrentLanguage():', getCurrentLanguage());
        window.location.assign(redirectUrl);
      }
    } else {
      // Handle regular URL navigation
      console.log('Regular navigation to:', href);
      window.location.assign(href);
    }
    
    // Close mobile menu if open
    setIsMenuOpen(false);
    setShowDropdown(false);
  };

  // Get current language from URL or default to 'lv'
  const getCurrentLanguage = () => {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const supportedLocales = ['lv', 'en', 'ru']; // Static list for URL parsing
    return supportedLocales.includes(pathParts[0]) ? pathParts[0] : 'lv';
  };

  const languageOptions = [
    { code: 'lv', label: t('language_switcher.lv'), flag: '🇱🇻' },
    { code: 'en', label: t('language_switcher.en'), flag: '🇬🇧' },
    { code: 'ru', label: t('language_switcher.ru'), flag: '🇷🇺' },
  ];

  const services = [
    { title: t('services.mortgage_loan'), icon: "home", slug: "mortgage", anchorId: "hipotekarais-kredits" },
    { title: t('services.business_development'), icon: "suitcase", slug: "business", anchorId: "biznesa-attistibai" },
    { title: t('services.loan_consolidation'), icon: "refresh", slug: "consolidation", anchorId: "kreditu-apvienosana" },
    { title: t('services.auto_leasing'), icon: "car", slug: "auto", anchorId: "auto-lizings" },
    { title: t('services.farmers'), icon: "crop", slug: "farmers", anchorId: "zemniekiem" },
    { title: t('services.construction'), icon: "building", slug: "construction", anchorId: "eku-buvniecibai" },
  ];

  const socialLinks = [
    { name: "facebook", icon: "facebook", link: "https://www.facebook.com/finlo.lv" },
    { name: "linkedin", icon: "linkedin", link: "https://www.linkedin.com/company/finlolv" },
  ];

  return (
    <header className="header sticky top-0 z-50 bg-primary-500 text-white shadow-card pb-5">
      {/* Header Contact - Desktop Only */}
      <div className="header-contact hidden lg:block bg-dark-500 py-2.5">
        <div className="container max-w-6xl mx-auto px-4 flex justify-between">
          <div className="left flex gap-10">
            <a 
              href="https://maps.app.goo.gl/VDzfR6BaiPSR5VEn7" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="address flex items-center gap-1.5 hover:opacity-70 transition-opacity"
            >
              <Icon name="map" className="w-4 h-4" />
              <span>{t('header.address')}</span>
            </a>
            <div className="working-hours flex items-center gap-1.5">
              <Icon name="clock" className="w-4 h-4" />
              <span>{t('header.working_hours')}</span>
            </div>
          </div>
          <div className="right flex gap-10">
            <a 
              href="tel:+37167660633" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="contact flex items-center gap-1.5 hover:opacity-70 transition-opacity"
            >
              <Icon name="phone" className="w-4 h-4" />
              <span>{t('header.phone')}</span>
            </a>
            <div className="socials flex items-center gap-2">
              {socialLinks.map((link) => (
                <a key={link.name} href={link.link} target="_blank" rel="noopener noreferrer" className="text-2xl hover:opacity-70 transition-opacity">
                  <Icon name={link.icon} className="w-6 h-6" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="navbar container max-w-6xl mx-auto px-4 flex justify-between items-center pt-5 lg:pt-0 lg:mt-5" id="home">
        <a 
          href={getLocalizedPath('/')} 
          className="logo cursor-pointer"
        >
          <img
            className="w-30 h-8 lg:w-41 lg:h-11"
            src="/finlo_logo.svg"
            alt={t('header.logo_alt')}
          />
        </a>

        {/* Mobile Menu Button */}
        <button 
          className="menu-button lg:hidden"
          onClick={toggleMenu}
        >
          <Icon name="menu" className="w-9 h-9" />
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center">
          <ul className="nav-list flex gap-8">
            <li>
              <a 
                href="/" 
                className="nav-item hover:text-black transition-colors cursor-pointer"
                onClick={(e) => handleNavClick('#home', e)}
              >
                {t('header.home')}
              </a>
            </li>
            <li className="services relative">
              <button 
                className="nav-item flex items-center gap-1 hover:text-black transition-colors"
                onClick={toggleDropdown}
              >
                <span>{t('header.services')}</span>
                <Icon name={showDropdown ? "chevronUp" : "chevronDown"} className="w-4 h-4" />
              </button>
              {showDropdown && (
                <ul className="service-list absolute top-full left-0 mt-2.5 p-6 bg-white text-black text-sm grid grid-cols-2 gap-3 shadow-lg rounded-xl z-10 min-w-96 border border-gray-100">
                  {services.map((service) => (
                    <li key={service.title}>
                      <a 
                        href={`#${service.anchorId}`}
                        className="service-item flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-all duration-200 cursor-pointer group"
                        onClick={(e) => {
                          e.preventDefault();
                          // Navigate to loan types page with anchor hash
                          const url = `${getLocalizedPath('/loan-types')}#${service.anchorId}`;
                          window.location.href = url;
                        }}
                      >
                        <Icon name={service.icon} className="w-5 h-5 text-primary-500 group-hover:text-primary-600 transition-colors" />
                        <span className="font-medium group-hover:text-primary-600 transition-colors">{service.title}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
            <li>
              <a 
                href="/#why-us" 
                className="nav-item hover:text-black transition-colors cursor-pointer"
                onClick={(e) => handleNavClick('#why-us', e)}
              >
                {t('header.why_us')}
              </a>
            </li>
            <li>
              <a 
                href="#faq" 
                className="nav-item hover:text-black transition-colors cursor-pointer"
                onClick={(e) => handleNavClick('#faq', e)}
              >
                {t('header.faq')}
              </a>
            </li>
            <li>
              <a 
                href={getLocalizedPath('/contact')} 
                className="nav-item hover:text-black transition-colors cursor-pointer"
              >
                {t('header.contacts')}
              </a>
            </li>
          </ul>
        </nav>

        {/* Language Switcher */}
        <div className="login-lang hidden lg:flex gap-4 items-center">
          <LanguageSwitcher 
            options={languageOptions}
            currentLanguage={getCurrentLanguage()}
          />
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="menu-open lg:hidden fixed top-0 left-0 w-full h-screen bg-primary-500 z-50">
          <button 
            className="close-button absolute top-8 left-8"
            onClick={toggleMenu}
          >
            <Icon name="close" className="w-8 h-8" />
          </button>
          
          <ul className="nav-list flex flex-col items-center gap-5 mt-20 text-center">
            <li>
              <a 
                href="#home" 
                className="nav-item text-2xl cursor-pointer" 
                onClick={(e) => handleNavClick('#home', e)}
              >
                {t('header.home')}
              </a>
            </li>
            <li className="services">
              <button 
                className="nav-item text-2xl flex items-center gap-2"
                onClick={toggleDropdown}
              >
                <span>{t('header.services')}</span>
                <Icon name={showDropdown ? "chevronUp" : "chevronDown"} className="w-6 h-6" />
              </button>
              {showDropdown && (
                <ul className="service-list mt-4 space-y-3">
                  {services.map((service) => (
                    <li key={service.title}>
                      <a 
                        href={`#${service.anchorId}`}
                        className="service-item flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 transition-all duration-200 text-white cursor-pointer group"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsMenuOpen(false);
                          // Navigate to loan types page with anchor hash
                          const url = `${getLocalizedPath('/loan-types')}#${service.anchorId}`;
                          window.location.href = url;
                        }}
                      >
                        <Icon name={service.icon} className="w-5 h-5 text-primary-500 group-hover:text-primary-600 transition-colors" />
                        <span className="font-medium group-hover:text-primary-600 transition-colors">{service.title}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
            <li>
              <a 
                href="#features" 
                className="nav-item text-2xl cursor-pointer" 
                onClick={(e) => handleNavClick('#features', e)}
              >
                {t('header.how_to_apply')}
              </a>
            </li>
            <li>
              <a 
                href="#why-us" 
                className="nav-item text-2xl cursor-pointer" 
                onClick={(e) => handleNavClick('#why-us', e)}
              >
                {t('header.why_us')}
              </a>
            </li>
            <li>
              <a 
                href="#faq" 
                className="nav-item text-2xl cursor-pointer" 
                onClick={(e) => handleNavClick('#faq', e)}
              >
                {t('header.faq')}
              </a>
            </li>
            <li>
              <a 
                href={getLocalizedPath('/contact')} 
                className="nav-item text-2xl cursor-pointer"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('header.contacts')}
              </a>
            </li>
          </ul>
          
          {/* Mobile Language Switcher */}
          <div className="mt-8 flex justify-center">
            <LanguageSwitcher 
              options={languageOptions}
              currentLanguage={getCurrentLanguage()}
              className="bg-white/10 rounded-lg"
            />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
